import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  estateApi,
  userApi,
  securityApi,
  adminManagementApi,
} from "../services/api";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Shield,
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  X,
  UserCog,
  AlertTriangle,
  Lock,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  can,
  isPlatformSuperAdmin,
  isEstateAdmin,
  getCurrentEstate,
} from "../utils/permissions";

const Estates = () => {
  const [selectedEstate, setSelectedEstate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    description: "",
    contactPhone: "",
    contactEmail: "",
    totalUnits: "",
    amenities: "",
  });

  // Nominatim autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const debounceTimer = useRef(null);

  const queryClient = useQueryClient();

  // Get current user's estate scope
  const currentEstate = getCurrentEstate();
  const isEstateManager = isEstateAdmin();
  const isPlatformAdmin = isPlatformSuperAdmin();

  // Fetch estates
  const {
    data: estates,
    isLoading: estatesLoading,
    error: estatesError,
  } = useQuery("estates", estateApi.getAll, {
    retry: 1,
    onError: (error) => {
      console.error("Error fetching estates:", error);
    },
  });

  // Fetch all users
  const { data: allUsers, isLoading: usersLoading } = useQuery(
    "users",
    userApi.getAll,
    {
      retry: 1,
      onError: (error) => {
        console.error("Error fetching users:", error);
      },
    },
  );

  // Fetch all security personnel
  const { data: allSecurity, isLoading: securityLoading } = useQuery(
    "security",
    securityApi.getAll,
    {
      retry: 1,
      onError: (error) => {
        console.error("Error fetching security:", error);
      },
    },
  );

  // Fetch all admins
  const { data: allAdmins, isLoading: adminsLoading } = useQuery(
    "admins",
    adminManagementApi.getAll,
    {
      retry: 1,
      onError: (error) => {
        console.error("Error fetching admins:", error);
      },
    },
  );

  // Calculate counts for each estate
  const estatesWithCounts = useMemo(() => {
    if (!estates || !Array.isArray(estates)) return [];

    let filteredEstates = estates;

    // Estate managers can only see their assigned estate
    if (isEstateManager && currentEstate) {
      filteredEstates = estates.filter(
        (estate) => estate._id === currentEstate,
      );
    }

    return filteredEstates.map((estate) => {
      const userCount = Array.isArray(allUsers)
        ? allUsers.filter((user) => {
            const userEstateId =
              typeof user.estate === "object" ? user.estate?._id : user.estate;
            return userEstateId === estate._id;
          }).length
        : 0;

      const securityCount = Array.isArray(allSecurity)
        ? allSecurity.filter((security) => {
            const securityEstateId =
              typeof security.estate === "object"
                ? security.estate?._id
                : security.estate;
            return securityEstateId === estate._id;
          }).length
        : 0;

      const adminCount = Array.isArray(allAdmins)
        ? allAdmins.filter((admin) => {
            if (Array.isArray(admin.managedEstates)) {
              return admin.managedEstates.some((estateId) => {
                const managedEstateId =
                  typeof estateId === "object" ? estateId._id : estateId;
                return managedEstateId === estate._id;
              });
            }
            const adminEstateId =
              typeof admin.estate === "object"
                ? admin.estate?._id
                : admin.estate;
            return adminEstateId === estate._id;
          }).length
        : 0;

      return {
        ...estate,
        userCount,
        securityCount,
        adminCount,
      };
    });
  }, [
    estates,
    allUsers,
    allSecurity,
    allAdmins,
    isEstateManager,
    currentEstate,
  ]);

  // Mutation to create estate
  const createEstate = useMutation((data) => estateApi.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("estates");
      toast.success("Estate created successfully!");
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      console.error("Error creating estate:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to create estate";
      toast.error(message);

      if (error.response?.status === 403) {
        toast.error("Not authorized to create estates");
      }
    },
  });

  // Update estate mutation
  const updateEstate = useMutation(
    (data) => estateApi.update(selectedEstate._id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("estates");
        toast.success("Estate updated successfully");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        const message =
          error.response?.data?.message || "Failed to update estate";
        toast.error(message);

        if (error.response?.status === 403) {
          toast.error("Not authorized to update this estate");
        }
      },
    },
  );

  const deleteEstate = useMutation(
    async (estateId) => {
      try {
        const response = await estateApi.delete(estateId);
        return response;
      } catch (error) {
        console.error("Delete error details:", {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });
        throw error;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("estates");
        queryClient.invalidateQueries("users");
        queryClient.invalidateQueries("security");
        queryClient.invalidateQueries("admins");
        toast.success("Estate deleted successfully");
      },
      onError: (error) => {
        console.error("Delete estate error:", error);

        let message = "Failed to delete estate";

        if (error.response?.status === 500) {
          message =
            "Server error: Cannot delete estate. It may have associated users, security personnel, or admins that need to be removed first.";
        } else if (error.response?.status === 403) {
          message = "Not authorized to delete estates";
        } else if (error.response?.status === 400) {
          message =
            error.response?.data?.message ||
            "Cannot delete estate with existing data";
        } else if (error.response?.status === 404) {
          message = "Estate not found";
        } else if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.response?.data?.error) {
          message = error.response.data.error;
        }

        toast.error(message, { duration: 5000 });
      },
    },
  );

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      description: "",
      contactPhone: "",
      contactEmail: "",
      totalUnits: "",
      amenities: "",
    });
    setSelectedEstate(null);
    setAddressSuggestions([]);
  };

  const handleCreate = () => {
    if (!can("estates", "create")) {
      toast.error("You do not have permission to create estates");
      return;
    }

    setModalType("create");
    setShowModal(true);
    resetForm();
  };

  const handleEdit = (estate) => {
    if (!can("estates", "update")) {
      toast.error("You do not have permission to edit estates");
      return;
    }

    setModalType("edit");
    setSelectedEstate(estate);
    setFormData({
      name: estate.name,
      address: estate.address,
      description: estate.description,
      contactPhone: estate.contactPhone,
      contactEmail: estate.contactEmail,
      totalUnits: estate.totalUnits,
      amenities: estate.amenities,
    });
    setShowModal(true);
  };

  const handleDelete = async (estate) => {
    if (!can("estates", "delete")) {
      toast.error("You do not have permission to delete estates");
      return;
    }

    const { userCount, securityCount, adminCount } = estate;

    if (userCount > 0 || securityCount > 0 || adminCount > 0) {
      let warningMessage = `⚠️ Cannot delete "${estate.name}"\n\nThis estate has:\n`;

      if (userCount > 0) warningMessage += `• ${userCount} resident(s)\n`;
      if (securityCount > 0)
        warningMessage += `• ${securityCount} security personnel\n`;
      if (adminCount > 0) warningMessage += `• ${adminCount} admin(s)\n`;

      warningMessage += `\n❌ You must remove or reassign all associated users, security, and admins before deleting this estate.`;

      toast.error(warningMessage, { duration: 8000 });
      return;
    }

    const confirmMessage =
      `🗑️ Delete Estate?\n\n` +
      `Estate: "${estate.name}"\n` +
      `Address: ${estate.address}\n\n` +
      `✅ This estate has no associated users, security, or admins.\n\n` +
      `⚠️ This action cannot be undone.\n\n` +
      `Are you sure you want to proceed?`;

    if (window.confirm(confirmMessage)) {
      deleteEstate.mutate(estate._id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (modalType === "create") {
      createEstate.mutate(formData);
    } else {
      updateEstate.mutate(formData);
    }
  };

  // Fetch suggestions from Nominatim (OpenStreetMap) — completely free, no API key
  const fetchNominatimSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=ng`,
        {
          headers: {
            // Nominatim requires a User-Agent header identifying your app
            "Accept-Language": "en",
          },
        },
      );
      const data = await response.json();
      setAddressSuggestions(data);
    } catch (error) {
      console.error("Nominatim error:", error);
      setAddressSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleAddressChange = (value) => {
    setFormData((prev) => ({ ...prev, address: value }));

    // Clear previous timer to debounce requests (Nominatim rate limit: 1 req/sec)
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!value) {
      setAddressSuggestions([]);
      return;
    }

    // Wait 500ms after user stops typing before hitting the API
    debounceTimer.current = setTimeout(() => {
      fetchNominatimSuggestions(value);
    }, 500);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "address") {
      handleAddressChange(value);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const isLoading =
    estatesLoading || usersLoading || securityLoading || adminsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (estatesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading estates
          </h3>
          <p className="text-gray-600 mb-4">
            Failed to load estates. Please try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Estate Management
          </h1>
          <p className="text-gray-600">
            {isEstateManager
              ? "Manage your assigned estate"
              : "Manage all estates in the system"}
          </p>
        </div>

        {can("estates", "create") && !isEstateManager && (
          <button
            onClick={handleCreate}
            className="btn btn-primary flex items-center"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Estate
          </button>
        )}
      </div>

      {/* Estate Manager Read-Only Notice */}
      {isEstateManager && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Lock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Estate Manager Access
              </p>
              <p className="text-sm text-blue-700 mt-1">
                You can only view and manage your assigned estate. To create or
                modify other estates, contact a Platform Super Admin.
              </p>
            </div>
          </div>
        </div>
      )}

      {!can("estates", "create") && !isPlatformAdmin && !isEstateManager && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            ℹ️ You have read-only access to estates. Contact a Platform Super
            Admin to create or modify estates.
          </p>
        </div>
      )}

      {/* Estates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(estatesWithCounts) &&
          estatesWithCounts.map((estate) => {
            const { userCount, securityCount, adminCount } = estate;
            const isDeletable =
              userCount === 0 && securityCount === 0 && adminCount === 0;

            return (
              <div
                key={estate._id}
                className={`card hover:shadow-lg transition-shadow ${
                  isEstateManager ? "ring-2 ring-blue-500" : ""
                }`}
              >
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="font-semibold text-gray-900">
                        {estate.name}
                        {isEstateManager && (
                          <span className="ml-2 inline-block px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded">
                            Your Estate
                          </span>
                        )}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      {can("estates", "update") && !isEstateManager && (
                        <button
                          onClick={() => handleEdit(estate)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Edit estate"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}

                      {can("estates", "delete") && !isEstateManager && (
                        <button
                          onClick={() => handleDelete(estate)}
                          className={`p-1 ${
                            isDeletable
                              ? "text-gray-400 hover:text-red-600"
                              : "text-gray-300 cursor-not-allowed"
                          }`}
                          title={
                            isDeletable
                              ? "Delete estate"
                              : "Cannot delete - has associated data"
                          }
                          disabled={!isDeletable}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        {estate.address}
                      </span>
                    </div>

                    {estate.contactPhone && (
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">
                          {estate.contactPhone}
                        </span>
                      </div>
                    )}

                    {estate.contactEmail && (
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">
                          {estate.contactEmail}
                        </span>
                      </div>
                    )}

                    {estate.totalUnits && (
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">
                          {estate.totalUnits} units
                        </span>
                      </div>
                    )}

                    {estate.description && (
                      <p className="text-sm text-gray-600 mt-2">
                        {estate.description}
                      </p>
                    )}
                  </div>

                  {/* Estate Stats */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-green-500 mr-2" />
                          <span className="text-sm text-gray-600">
                            Residents
                          </span>
                        </div>
                        <span
                          className={`text-sm font-semibold ${
                            userCount > 0 ? "text-green-600" : "text-gray-400"
                          }`}
                        >
                          {userCount}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Shield className="h-4 w-4 text-purple-500 mr-2" />
                          <span className="text-sm text-gray-600">
                            Security
                          </span>
                        </div>
                        <span
                          className={`text-sm font-semibold ${
                            securityCount > 0
                              ? "text-purple-600"
                              : "text-gray-400"
                          }`}
                        >
                          {securityCount}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <UserCog className="h-4 w-4 text-blue-500 mr-2" />
                          <span className="text-sm text-gray-600">Admins</span>
                        </div>
                        <span
                          className={`text-sm font-semibold ${
                            adminCount > 0 ? "text-blue-600" : "text-gray-400"
                          }`}
                        >
                          {adminCount}
                        </span>
                      </div>
                    </div>

                    {!isEstateManager && !isDeletable && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-start space-x-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>Remove all users before deleting</span>
                        </div>
                      </div>
                    )}

                    {!isEstateManager && isDeletable && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-center text-xs text-green-600 bg-green-50 p-2 rounded">
                          <span>✓ Ready to delete</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Empty State */}
      {(!estatesWithCounts ||
        !Array.isArray(estatesWithCounts) ||
        estatesWithCounts.length === 0) && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isEstateManager ? "Estate not found" : "No estates found"}
          </h3>
          <p className="text-gray-600 mb-4">
            {isEstateManager
              ? "Your assigned estate could not be found. Please contact your administrator."
              : can("estates", "create")
                ? "Get started by creating your first estate."
                : "No estates are available. Contact your administrator."}
          </p>

          {can("estates", "create") && !isEstateManager && (
            <button onClick={handleCreate} className="btn btn-primary">
              <Plus className="mr-2 h-4 w-4" />
              Add Estate
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalType === "create" ? "Create New Estate" : "Edit Estate"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">
                    Estate Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Contact Phone</label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Address field with Nominatim autocomplete */}
              <div className="relative">
                <label className="form-label">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  autoComplete="off"
                  placeholder="Start typing an address..."
                />

                {/* Loading indicator */}
                {isLoadingSuggestions && (
                  <div className="absolute z-10 bg-white border border-gray-200 w-full mt-1 px-3 py-2 text-sm text-gray-500 rounded shadow">
                    Searching...
                  </div>
                )}

                {/* Suggestions dropdown */}
                {!isLoadingSuggestions && addressSuggestions.length > 0 && (
                  <ul className="absolute z-10 bg-white border border-gray-200 w-full mt-1 max-h-48 overflow-auto rounded shadow-lg">
                    {addressSuggestions.map((suggestion) => (
                      <li
                        key={suggestion.place_id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-b-0"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            address: suggestion.display_name,
                          }));
                          setAddressSuggestions([]);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>{suggestion.display_name}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="form-label">Contact Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Total Units</label>
                  <input
                    type="number"
                    name="totalUnits"
                    value={formData.totalUnits}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label">Amenities</label>
                  <input
                    type="text"
                    name="amenities"
                    value={formData.amenities}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Pool, Gym, Parking..."
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createEstate.isLoading || updateEstate.isLoading}
                  className="btn btn-primary"
                >
                  {createEstate.isLoading || updateEstate.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      {modalType === "create" ? "Creating..." : "Updating..."}
                    </>
                  ) : modalType === "create" ? (
                    "Create Estate"
                  ) : (
                    "Update Estate"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Estates;
